﻿import React, {FC, useEffect, useRef} from 'react';

interface WithLoaderProps {
  visible?: boolean;
  isLoading: boolean;
}

export const withLoader = <T,>(Component: FC<T>, LoaderComponent: FC<any>): FC<T & WithLoaderProps> => {

  const WithLoader = (props: T & WithLoaderProps) => {
    const {
      visible = true,
      isLoading
    } = props;

    /**
     * Skeletons are for the first load only. Once a request has settled, later
     * loads (filter change, pagination) keep the previous content on screen
     * until the new data lands — the request hooks hold onto the last payload,
     * so it is still there to render. Swapping back to skeletons on every
     * refetch made the list flash away and back on each filter keystroke.
     *
     * A load only counts as settled if the list is visible when it lands:
     * callers hide it on error (`visible={!error}`), and a failed request
     * leaves no payload to retain, so the next attempt still needs a skeleton.
     */
    const hasSettled = useRef(false);
    const wasLoading = useRef(false);
    useEffect(() => {
      if (wasLoading.current && !isLoading && visible) {
        hasSettled.current = true;
      }
      wasLoading.current = isLoading;
    }, [isLoading, visible]);

    const showLoader = isLoading && !hasSettled.current;

    return <>
      {
        visible && (showLoader ? <LoaderComponent {...props}/> : <Component {...props} />)
      }
    </>
  }

  WithLoader.displayName = `withLoader(${Component.displayName || Component.name || 'Component'}`;
  return WithLoader;
}
